/**
 * Web Bluetooth 传输：BLE 数传模块透传。
 *
 * 适用场景：机载 BLE 透传模块（HC-05 类、ESP32 BLE 串口桥、部分数传电台的手机端）。
 * 它解决的是"没有 USB 口可插、也没有网络"的场合。
 *
 * 与串口最大的差别是**写特征有 MTU 上限**：一次 `writeValue` 超过 ATT MTU 会被拒绝，
 * 而 MAVLink 帧动辄两三百字节（`FILE_TRANSFER_PROTOCOL` 254 字节、
 * `AUTOPILOT_VERSION` 78 字节）。不做分包的话表现是"连上了，但只有小消息能过"，
 * 极难排查 —— 所以分包是这里的核心职责，不是可选优化。
 */
import { BaseTransport, type TransportKind, type TransportMetrics } from './transport';

/** Nordic UART Service —— BLE 串口透传的事实标准 */
export const NORDIC_UART_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
/** 写特征（从外设角度看是 RX） */
export const NORDIC_UART_RX_CHARACTERISTIC = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';
/** 通知特征（从外设角度看是 TX） */
export const NORDIC_UART_TX_CHARACTERISTIC = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';

/**
 * 默认分包大小。
 *
 * BLE 4.2 的默认 ATT MTU 是 23，减去 3 字节 ATT 头只剩 20 字节；
 * 协商到更大的 MTU 后可以发更多，但协商结果无法从 Web Bluetooth 拿到。
 * 取 20 是保守且普遍可行的值；模块数传本身带宽就很低，差别不大。
 */
export const DEFAULT_BLE_CHUNK_SIZE = 20;

export interface BluetoothTransportOptions {
  /** 已授权的设备（免手势路径） */
  readonly device?: BluetoothDevice;
  readonly serviceUuid?: string;
  readonly writeCharacteristicUuid?: string;
  readonly notifyCharacteristicUuid?: string;
  readonly chunkSize?: number;
  /** 注入蓝牙实现，供测试使用 */
  readonly bluetooth?: Bluetooth;
}

function bluetoothApi(): Bluetooth | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { bluetooth?: Bluetooth }).bluetooth ?? null;
}

export class BluetoothTransport extends BaseTransport {
  readonly kind: TransportKind = 'bluetooth';

  private device: BluetoothDevice | null;
  private writeCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;
  private readonly serviceUuid: string;
  private readonly writeUuid: string;
  private readonly notifyUuid: string;
  private readonly chunkSize: number;
  private readonly injected: Bluetooth | null;
  private deviceName: string | null = null;
  private rssi: number | null = null;

  private readonly onDisconnected = (): void => {
    this.writeCharacteristic = null;
    this.setState('error', '蓝牙设备已断开（超出范围或设备侧断开）。');
  };

  constructor(options: BluetoothTransportOptions = {}) {
    super();
    this.device = options.device ?? null;
    this.serviceUuid = options.serviceUuid ?? NORDIC_UART_SERVICE;
    this.writeUuid = options.writeCharacteristicUuid ?? NORDIC_UART_RX_CHARACTERISTIC;
    this.notifyUuid = options.notifyCharacteristicUuid ?? NORDIC_UART_TX_CHARACTERISTIC;
    this.chunkSize = Math.max(1, options.chunkSize ?? DEFAULT_BLE_CHUNK_SIZE);
    this.injected = options.bluetooth ?? null;
  }

  get label(): string {
    return this.deviceName === null ? '蓝牙串口' : `蓝牙 · ${this.deviceName}`;
  }

  linkMetrics(): TransportMetrics {
    return { latencyMs: null, rssi: this.rssi };
  }

  private api(): Bluetooth | null {
    return this.injected ?? bluetoothApi();
  }

  /** 必须在用户手势中调用 */
  async requestDevice(): Promise<BluetoothDevice> {
    const api = this.api();
    if (api === null) {
      throw new Error('当前浏览器不支持 Web Bluetooth。请使用桌面版 Chrome 或 Edge。');
    }
    const device = await api.requestDevice({
      filters: [{ services: [this.serviceUuid] }],
      optionalServices: [this.serviceUuid],
    });
    this.device = device;
    this.deviceName = device.name ?? null;
    return device;
  }

  override async open(): Promise<void> {
    const api = this.api();
    if (api === null) {
      const detail = '当前浏览器不支持 Web Bluetooth。请使用桌面版 Chrome 或 Edge。';
      this.setState('error', detail);
      throw new Error(detail);
    }

    this.setState('opening');

    const device = this.device;
    if (device === null) {
      const detail = '尚未选择蓝牙设备。请点击"选择设备"并授权一次。';
      this.setState('error', detail);
      throw new Error(detail);
    }

    this.deviceName = device.name ?? this.deviceName;

    try {
      const server = device.gatt;
      if (server === undefined) {
        throw new Error('设备没有提供 GATT 服务');
      }
      const connection = await server.connect();
      device.removeEventListener('gattserverdisconnected', this.onDisconnected);
      device.addEventListener('gattserverdisconnected', this.onDisconnected);

      const service = await connection.getPrimaryService(this.serviceUuid);
      const writeCharacteristic = await service.getCharacteristic(this.writeUuid);
      this.writeCharacteristic = writeCharacteristic;

      const notifyCharacteristic = await service.getCharacteristic(this.notifyUuid);
      await notifyCharacteristic.startNotifications();
      notifyCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
        const target = event.target as BluetoothRemoteGATTCharacteristic | null;
        const value = target?.value;
        if (value === undefined || value === null) return;
        // 必须拷贝：这个 DataView 在下一个通知到来时会被底层复用
        this.emitData(new Uint8Array(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength)));
      });
    } catch (error) {
      const detail = this.explainFailure(error);
      this.setState('error', detail);
      throw new Error(detail);
    }

    this.setState('open');
  }

  private explainFailure(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error);
    if (/not found|no service/i.test(message)) {
      return `设备上没有找到透传服务 ${this.serviceUuid}。它可能不是 BLE 串口模块，或需要先配对。`;
    }
    if (/gatt|disconnected|connect/i.test(message)) {
      return `连接蓝牙设备失败：${message}。请确认设备已开机且在有效范围内。`;
    }
    return `连接蓝牙设备失败：${message}`;
  }

  override async close(): Promise<void> {
    this.device?.removeEventListener('gattserverdisconnected', this.onDisconnected);
    this.writeCharacteristic = null;
    try {
      this.device?.gatt?.disconnect();
    } catch {
      /* 已经断开时 disconnect 会抛错，忽略 */
    }
    this.setState('closed');
  }

  override async write(bytes: Uint8Array): Promise<void> {
    const characteristic = this.writeCharacteristic;
    if (characteristic === null) {
      throw new Error('蓝牙链路未打开，无法发送');
    }

    // 分包顺序发送：BLE 的写特征是顺序队列，并发写会互相覆盖。
    // 每片都重新拷一份：`subarray` 得到的是视图，其底层缓冲可能是 SharedArrayBuffer，
    // 而平台写入接口只接受普通的 ArrayBufferView。BLE 本身很慢，这点拷贝无关紧要。
    for (let offset = 0; offset < bytes.length; offset += this.chunkSize) {
      const chunk = new Uint8Array(
        bytes.subarray(offset, Math.min(offset + this.chunkSize, bytes.length)),
      );
      if (typeof characteristic.writeValueWithoutResponse === 'function') {
        await characteristic.writeValueWithoutResponse(chunk);
      } else {
        await characteristic.writeValue(chunk);
      }
    }
  }
}
